import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Sparkles, Shield } from 'lucide-react';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, loading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      toast('Signed in successfully', 'success');
      navigate('/');
    } catch (err: any) {
      const msg = err?.code === 'auth/invalid-credential'
        ? 'Invalid email or password'
        : err?.message || 'Failed to sign in';
      toast(msg, 'error');
    }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md animate-fade-in relative">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-11 h-11 gradient-blue rounded-2xl flex items-center justify-center shadow-lg glow-blue">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="text-2xl font-bold gradient-text-blue">OBAET</span>
            <span className="text-2xl font-bold text-[#f1f5f9]"> Admin</span>
          </div>
        </div>

        {/* Card */}
        <div className="glass-card rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-7">
            <h1 className="text-xl font-semibold text-[#f1f5f9]">Welcome back</h1>
            <p className="text-sm text-[#94a3b8] mt-1.5">
              Sign in to access the admin dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              placeholder="admin@obaet.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <Input
              label="Password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <div className="pt-1">
              <Button
                type="submit"
                loading={loading}
                disabled={!email || !password}
                className="w-full !py-2.5 !text-sm"
              >
                Sign In
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-[#252535]">
            <div className="flex items-center justify-center gap-2 text-xs text-[#64748b]">
              <Shield className="w-3.5 h-3.5" />
              <span>Admin access only. Contact your administrator for access.</span>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-[#64748b] mt-6">
          OBAET &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
